import { useState, useEffect } from 'react';

/**
 * Custom hook to debounce loading state display to prevent jarring flashes
 * @param isLoading - The actual loading state from API calls
 * @param delay - Minimum delay before showing loading state (default: 300ms)
 * @returns Debounced loading state that only shows after the delay
 */
const useDebouncedLoading = (isLoading: boolean, delay = 300): boolean => {
  const [debouncedLoading, setDebouncedLoading] = useState(false);

  useEffect(() => {
    if (isLoading) {
      // Start a timer to show loading after delay
      const timer = setTimeout(() => {
        setDebouncedLoading(true);
      }, delay);

      // Cleanup timer if loading completes before delay
      return () => clearTimeout(timer);
    }

    // Immediately hide loading when done
    setDebouncedLoading(false);
    return undefined;
  }, [isLoading, delay]);

  return debouncedLoading;
};

export default useDebouncedLoading;